import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import Header from '#/components/Form/Header';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import { TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import {
  IArtistAlbums,
  IArtistResponse,
  IArtistTopTracksResponse,
} from '#/types/API/artists';
import { header2ClassName, spotifyApiConfig } from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import tinyColor from 'tinycolor2';
import Vibrant from 'node-vibrant';
import PlayButton from '#/components/Form/PlayButton/PlayButton';
import PreviewLayout from '#/components/Layout/PreviewLayout/PreviewLayout';
import { getItemsPerBreakpoint } from '#/utils/getItemsPerBreakpoint';
import useViewport from '#/hooks/useViewport';
import { ROUTE_NAMES } from '#/router/routes';
import LoadingPage from '#/components/Layout/LoadingPage/LoadingPage';
import TopItemPreview from '#/components/Layout/TopItemPreview/TopItemPreview';
import { IAlbumResponse } from '#/types/API/albums';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import ITrackResponse from '#/types/API/tracks';
import {
  createActionTrackReset,
  createActionTrackSetAlbumTrackFavorite,
  createActionTrackSetDetails,
  createActionTrackSetError,
  createActionTrackSetFavorite,
  createActionTrackSetTopTrackFavorite,
  createActionTrackStartLoading,
  createActionTrackStopLoading,
} from '#/redux/actions/track';
import msToReadableTime from '#/utils/msToReadableTime';
import ArtistBanner from './components/ArtistBanner/ArtistBanner';
import AlbumBanner from './components/AlbumBanner/AlbumBanner';
import ArtistPreview from '#/components/Layout/ArtistPreview/ArtistPreview';
import useDeviceId from '#/hooks/useDeviceId';
import {
  emptyContainsResponse,
  emptyRelatedArtistsResponse,
  emptyTopTracksResponse,
  okResponse,
  urisParameter,
} from '#/utils/spotifyApiHelpers';

const trackSelector = (state: TRootState) => state.track;

type TColor = [number, number, number];

const Track = () => {
  const [expandTopTracks, setExpandTopTracks] = useState(false);

  const navigate = useNavigate();

  const imageReference = useRef<HTMLImageElement>(null);

  const { viewportWidth } = useViewport();

  const [dominantColor, setDominantColor] = useState<undefined | TColor>();

  const trackSlice = useSelector(trackSelector);

  const {
    track,
    album,
    artist,
    relatedArtists,
    topTracks,
    otherAlbums,
    _error,
    _loading,
  } = trackSlice;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const { id } = useParams();

  const otherAlbumsLimit = 6;

  const { retrieveDeviceId } = useDeviceId();

  const handleClickPlay = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerPlay',
      '',
      {
        context_uri: track?.album.uri,
        uris: [track?.uri],
      },
      {
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant reproduce');
    }
  };

  useEffect(() => {
    const getTrackDetails = async () => {
      dispatch(createActionTrackStartLoading());
      if (id) {
        const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const responseTrack = await apiMannager.getEntity<ITrackResponse>(
          'tracks',
          id
        );

        if ('error' in responseTrack) {
          // TODO: handle error
          dispatch(createActionTrackSetError(true));
        } else {
          const trackData = responseTrack.data;

          const responseArtist = await apiMannager.getEntity<IArtistResponse>(
            'artists',
            trackData.artists[0].id
          );

          const responseAlbum = await apiMannager.getEntity<IAlbumResponse>(
            'albums',
            trackData.album.id
          );

          const responseTopTracks = await okResponse<IArtistTopTracksResponse>(
            emptyTopTracksResponse
          );

          const responseRelatedArtists = await okResponse(
            emptyRelatedArtistsResponse
          );

          if (
            'error' in responseAlbum ||
            'error' in responseTopTracks ||
            'error' in responseRelatedArtists ||
            'error' in responseArtist
          ) {
            // TODO: handle error
            dispatch(createActionTrackSetError(true));
          } else {
            const artistData = responseArtist.data;

            const topTracksData = responseTopTracks.data.tracks;

            const albumData = responseAlbum.data;

            const relatedArtistsData = responseRelatedArtists.data.artists;

            const responseOtherAlbums =
              await apiMannager.getEntity<IArtistAlbums>(
                'artists',
                `${trackData.artists[0].id}/albums`,
                { limit: otherAlbumsLimit }
              );

            const responseIsFavorite = await apiMannager.getAllEntities<
              boolean[]
            >('libraryContains', {
              uris: urisParameter('track', id),
            });

            const topTrackIds = topTracksData.map((topTrack) => topTrack.id);
            const responseTopTracksFavorites =
              topTrackIds.length > 0
                ? await apiMannager.getAllEntities<boolean[]>(
                    'libraryContains',
                    {
                      uris: urisParameter('track', topTrackIds),
                    }
                  )
                : await emptyContainsResponse();

            const albumTrackIds = albumData.tracks.items.map(
              (track) => track.id
            );
            const responseAlbumTracksFavorites =
              albumTrackIds.length > 0
                ? await apiMannager.getAllEntities<boolean[]>(
                    'libraryContains',
                    {
                      uris: urisParameter('track', albumTrackIds),
                    }
                  )
                : await emptyContainsResponse();
            if (
              'error' in responseIsFavorite ||
              'error' in responseOtherAlbums ||
              'error' in responseTopTracksFavorites ||
              'error' in responseAlbumTracksFavorites
            ) {
              // TODO: handle error
              dispatch(createActionTrackSetError(true));
            } else {
              const otherAlbumsData = responseOtherAlbums.data.items;

              const topTracksFavorites = responseTopTracksFavorites.data;

              const topTracksWithFavorites = topTracksData.map(
                (topTrack, index) => ({
                  ...topTrack,
                  favorite: topTracksFavorites[index],
                })
              );

              const albumTracksFavorites = responseAlbumTracksFavorites.data;

              const albumTracksWithFavorites = albumData.tracks.items.map(
                (track, index) => ({
                  ...track,
                  favorite: albumTracksFavorites[index],
                })
              );

              albumData.tracks.items = albumTracksWithFavorites;

              const isFavorite = responseIsFavorite.data[0];

              dispatch(
                createActionTrackSetDetails({
                  track: { ...trackData, favorite: isFavorite },
                  otherAlbums: otherAlbumsData,
                  topTracks: topTracksWithFavorites,
                  album: albumData,
                  relatedArtists: relatedArtistsData,
                  artist: artistData,
                })
              );

              dispatch(createActionTrackStopLoading());
            }
          }
        }
      }
    };

    getTrackDetails();
  }, [dispatch, id, protectedSpotify]);

  useEffect(() => {
    if (album) {
      Vibrant.from(album.images[0].url)
        .getPalette()
        .then((palette) => setDominantColor(palette.Vibrant?.getRgb()));
    }
  }, [album]);

  useEffect(() => {
    return () => {
      dispatch(createActionTrackReset());
    };
  }, [dispatch]);

  const handleClickFavorite = async () => {
    const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);
    if (track?.favorite) {
      const responseRemoveFromFavorites = await apiMannager.deleteEntity(
        'library',
        '',
        {
          uris: urisParameter('track', track.id),
        }
      );

      if ('error' in responseRemoveFromFavorites) {
        //TODO: handle error
      } else {
        dispatch(
          createActionTrackSetFavorite({
            favorite: false,
          })
        );
      }
    } else {
      const responseAddToFavorites = await apiMannager.replaceEntity(
        'library',
        '',
        undefined,
        {
          uris: urisParameter('track', track?.id ?? ''),
        }
      );

      if ('error' in responseAddToFavorites) {
        //TODO: handle error
      } else {
        //
        dispatch(
          createActionTrackSetFavorite({
            favorite: true,
          })
        );
      }
    }
  };

  const getBannerStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const color = tinyColor(`rgb(${dominantColor.join(', ')})`);
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(22);
      style.backgroundImage = `linear-gradient(${color.toRgbString()}, ${darkerDominant.toRgbString()})`;
    }
    return style;
  };

  const getBodyStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(28);
      style.backgroundImage = `linear-gradient(${darkerDominant.toRgbString()}, transparent 240px)`;
    }
    return style;
  };

  return (
    <>
      {_loading || _error ? (
        <LoadingPage />
      ) : (
        <div className="mt-theme-top-bar-negative-height relative">
          <div className=" h-16 w-full sticky top-0 pb-4 bg-theme-darker-gray" />
          <div className="pt-theme-top-bar-height" style={getBannerStyle()}>
            <div className="flex gap-6 pt-9 p-theme-page-padding">
              <div className=" w-[192px] 2xl:w-[232px]">
                <div className="w-full aspect-square overflow-hidden shadow-2xl shadow-theme-shadow">
                  <img
                    className="  h-full w-full object-cover"
                    ref={imageReference}
                    src={album?.images[0].url}
                    alt=""
                  />
                </div>
              </div>
              <div className=" flex-1 flex flex-col justify-end pb-1">
                <span className="font-bold text-sm">Song</span>
                <Header
                  type="h1"
                  className="text-6xl mb-9 font-bold tracking-tighter"
                >
                  {track?.name}
                </Header>
                <div className="flex gap-1 text-sm items-center">
                  <div className="w-6 aspect-square rounded-full overflow-hidden">
                    <img
                      className="object-cover"
                      src={artist?.images[2].url}
                      alt=""
                    />
                  </div>
                  <Link to={`${ROUTE_NAMES.ARTIST}/${album?.artists[0].id}`}>
                    <span className="hover:underline font-bold">
                      {album?.artists[0].name}
                    </span>
                  </Link>
                  •
                  <Link to={`${ROUTE_NAMES.ALBUM}/${album?.id}`}>
                    <span className="hover:underline">{album?.name}</span>
                  </Link>
                  •<span>{album?.release_date.slice(0, 4)}</span>•
                  <span>{msToReadableTime(track ? track.duration_ms : 0)}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            className="min-h-full p-theme-page-padding"
            style={getBodyStyle()}
          >
            <div className="flex flex-row items-center gap-8 mb-theme-page-padding">
              <PlayButton onClick={handleClickPlay} />
              <span
                onClick={handleClickFavorite}
                className={`material-icons text-[32px] cursor-default ${
                  track?.favorite
                    ? 'text-theme-green'
                    : 'text-theme-light-gray hover:text-theme-white'
                } `}
              >
                {track?.favorite ? 'favorite' : 'favorite_border'}
              </span>
            </div>
            <div className=" mb-10">
              <ArtistBanner
                id={artist?.id ?? ''}
                image={artist?.images[0].url}
                name={artist?.name ?? ''}
              />
              <Header
                type="h2"
                className="text-theme-light-gray text-base mt-10"
              >
                Populars Tracks by
              </Header>
              <Header type="h2" className={header2ClassName}>
                {artist?.name}
              </Header>
              {topTracks?.map((track, index) => {
                const handleClickFavoriteTrack = async () => {
                  const apiMannager = new ApiManager(
                    spotifyApiConfig,
                    protectedSpotify
                  );
                  if (track.favorite) {
                    const responseRemoveFromFavorites =
                      await apiMannager.deleteEntity('library', '', {
                        uris: urisParameter('track', track.id),
                      });

                    if ('error' in responseRemoveFromFavorites) {
                      //TODO: handle error
                    } else {
                      dispatch(
                        createActionTrackSetTopTrackFavorite({
                          track: track.id,
                          favorite: false,
                        })
                      );
                    }
                  } else {
                    const responseAddToFavorites =
                      await apiMannager.replaceEntity(
                        'library',
                        '',
                        undefined,
                        {
                          uris: urisParameter('track', track.id),
                        }
                      );

                    if ('error' in responseAddToFavorites) {
                      //TODO: handle error
                    } else {
                      //
                      dispatch(
                        createActionTrackSetTopTrackFavorite({
                          track: track.id,
                          favorite: true,
                        })
                      );
                    }
                  }
                };

                return expandTopTracks || index <= 4 ? (
                  <TrackListItem
                    uri={track.uri}
                    contextUri={track.album.uri}
                    id={track.id}
                    onFavorite={handleClickFavoriteTrack}
                    key={track.id}
                    explicit={track.explicit}
                    image={track.album.images[0].url}
                    name={track.name}
                    duration={track.duration_ms}
                    number={index + 1}
                    followed={track.favorite}
                  />
                ) : undefined;
              })}
              <span
                className=" cursor-pointer ml-4 text-theme-light-gray hover:text-theme-white"
                onClick={() =>
                  setExpandTopTracks((previousExpand) => !previousExpand)
                }
              >
                {topTracks &&
                  topTracks.length >= 5 &&
                  `See ${expandTopTracks ? 'less' : 'more'}`}
              </span>
            </div>
            <div className="mt-10">
              <div>
                <div className="flex justify-between items-center">
                  <Header type="h2" className={header2ClassName}>
                    More by {artist?.name}
                  </Header>
                  <Link
                    className="link"
                    to={`${ROUTE_NAMES.ARTIST}/${album?.artists[0].id}/discography`}
                  >
                    Show all
                  </Link>
                </div>
                <PreviewLayout>
                  {otherAlbums?.map((album, index) => {
                    if (index >= getItemsPerBreakpoint(viewportWidth)) {
                      return;
                    }

                    const handleOnClick = () =>
                      navigate(`${ROUTE_NAMES.ALBUM}/${album.id}`);

                    return (
                      <TopItemPreview
                        uri={album.uri}
                        key={album.id}
                        name={album.name}
                        image={album.images[0].url}
                        releaseYear={Number(album.release_date.slice(0, 4))}
                        onClick={handleOnClick}
                      />
                    );
                  })}
                </PreviewLayout>
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <Header type="h2" className={header2ClassName}>
                    Fans also like
                  </Header>
                  <Link
                    className="link"
                    to={`${ROUTE_NAMES.ARTIST}/${id}/related`}
                  >
                    Show all
                  </Link>
                </div>
                <PreviewLayout>
                  {relatedArtists?.map((artist, index) => {
                    if (index >= getItemsPerBreakpoint(viewportWidth)) {
                      return;
                    }
                    const { id, name, images, uri } = artist;

                    const handleOnClick = () =>
                      navigate(`${ROUTE_NAMES.ARTIST}/${id}`);

                    return (
                      <ArtistPreview
                        uri={uri}
                        key={id}
                        onClick={handleOnClick}
                        name={name}
                        image={images[0].url}
                      />
                    );
                  })}
                </PreviewLayout>
              </div>
              <AlbumBanner
                id={album?.id ?? ''}
                name={album?.name ?? ''}
                image={album?.images[0].url}
              />
              {album?.tracks.items?.map((track, index) => {
                const handleClickFavoriteTrack = async () => {
                  const apiMannager = new ApiManager(
                    spotifyApiConfig,
                    protectedSpotify
                  );
                  if (track.favorite) {
                    const responseRemoveFromFavorites =
                      await apiMannager.deleteEntity('library', '', {
                        uris: urisParameter('track', track.id),
                      });

                    if ('error' in responseRemoveFromFavorites) {
                      //TODO: handle error
                    } else {
                      dispatch(
                        createActionTrackSetAlbumTrackFavorite({
                          track: track.id,
                          favorite: false,
                        })
                      );
                    }
                  } else {
                    const responseAddToFavorites =
                      await apiMannager.replaceEntity(
                        'library',
                        '',
                        undefined,
                        {
                          uris: urisParameter('track', track.id),
                        }
                      );

                    if ('error' in responseAddToFavorites) {
                      //TODO: handle error
                    } else {
                      //
                      dispatch(
                        createActionTrackSetAlbumTrackFavorite({
                          track: track.id,
                          favorite: true,
                        })
                      );
                    }
                  }
                };

                return (
                  <TrackListItem
                    uri={track.uri}
                    contextUri={album.uri}
                    id={track.id}
                    key={track.id}
                    name={track.name}
                    duration={track.duration_ms}
                    explicit={track.explicit}
                    number={index + 1}
                    followed={track.favorite ?? false}
                    artists={track.artists.map((artist) => ({
                      name: artist.name,
                      id: artist.id,
                    }))}
                    onFavorite={handleClickFavoriteTrack}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Track;
